import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dnsHostHint,
  DOMAIN_STATE_BADGE,
  asciiName,
  domainStateLine,
  normalizeDomain,
  recordsText,
  type DomainRecordRow,
  type SendingDomainView,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Button } from '../ui/Button';
import { CopyButton } from '../ui/CopyButton';
import { Notice, StatusBadge } from '../ui/Badge';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { useLanding } from '../ui/useLanding';
import { useNow } from '../ui/useNow';
import { Field, field } from '../create/Field';
import { IconMail } from '../icons';
import { fieldErrorOf } from './CustomerRepliesField';
import { EMAIL_ANCHOR } from './emailView';
import {
  DOMAIN_STATE_TONE,
  RECORD_PURPOSE,
  RECORD_STATUS,
  checkAgainIn,
  domainTestLine,
  lastCheckedLine,
  removeLine,
} from './domainView';

/** While the card is open, "Your domain" asks again this often (the server checks every 10 min). */
export const DOMAIN_REFRESH_MS = 60_000;

/**
 * "Your domain" in Settings → Email (`#email-domain`, MAP 249 spec §7, ticket 08), under
 * "Sending from". The owner sets it up (the domain and the address customers see), adds the four
 * records where the domain's DNS is managed (each value with its own Copy, and Copy all for
 * whoever runs it), presses Check again (at most once a minute; the server checks every 10
 * minutes anyway), sends a test once it is verified, or disconnects it. The office reads it all.
 *
 * Everything said here is the server's (`emailDomain.get`): the state, the records and whether
 * each was found, the DNS provider's hint, the optional DMARC suggestion. A read that did not come
 * back shows the read's own state, never "not set up".
 */
export function DomainSetup({
  owner,
  on,
  shopName,
}: {
  owner: boolean;
  /** Can customer email leave at all (`email.status.on`)? */
  on: boolean;
  shopName: string | null;
}) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.emailDomain.get.queryOptions(),
    refetchOnMount: 'always',
    refetchInterval: DOMAIN_REFRESH_MS,
  });
  const landing = useLanding<HTMLDivElement>(EMAIL_ANCHOR.domain);
  // A panel that replaces the one a press removed takes the focus (LESSONS 2026-09-28).
  const [arrived, setArrived] = useState<'added' | 'disconnected' | null>(null);
  return (
    <div
      ref={landing.ref}
      id={EMAIL_ANCHOR.domain}
      data-landed={landing.lit ? 'true' : undefined}
      className={`mt-3 scroll-mt-[var(--sw-anchor-offset)] rounded-[var(--radius)] border border-border p-3 transition-colors ${landing.mark}`}
    >
      <h4 className="mb-1 text-sm font-medium">Your domain</h4>
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {q.data === undefined ? (
        <UnresolvedState
          state={unresolved(shownState(q, q.data))}
          what="your domain"
          icon={IconMail}
          error={q.error}
          onRetry={() => void q.refetch()}
          skeleton="h-16"
        />
      ) : q.data === null ? (
        <NoDomain
          owner={owner}
          on={on}
          shopName={shopName}
          focusOpener={arrived === 'disconnected'}
          onAdded={() => setArrived('added')}
        />
      ) : (
        <DomainPanel
          v={q.data}
          owner={owner}
          focusState={arrived === 'added'}
          onDisconnected={() => setArrived('disconnected')}
        />
      )}
    </div>
  );
}

/** No domain: a sentence, and for the owner the way to set one up. */
function NoDomain({
  owner,
  on,
  shopName,
  focusOpener,
  onAdded,
}: {
  owner: boolean;
  on: boolean;
  shopName: string | null;
  focusOpener: boolean;
  onAdded: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState('');
  const [local, setLocal] = useState('office');
  const [error, setError] = useState<string | null>(null);
  const helpId = useId();
  const errorId = useId();
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusOpener) opener.current?.focus();
  }, [focusOpener]);
  const add = useMutation(
    trpc.emailDomain.add.mutationOptions({
      onSuccess: () => {
        setError(null);
        onAdded();
        void qc.invalidateQueries();
      },
      onError: (e) => setError(fieldErrorOf(e.message)),
    }),
  );
  const intro = (
    <p className="text-xs text-muted-foreground">
      Send your customer emails from your own address, like office@joesplumbing.com, once
      you add four records where your domain is managed.
    </p>
  );
  if (!owner || !on) {
    return (
      <>
        <p className="text-sm">Not set up.</p>
        {owner ? (
          <p className="text-xs text-muted-foreground">
            It can be set up once email is switched on.
          </p>
        ) : null}
      </>
    );
  }
  if (!open) {
    return (
      <div className="space-y-2">
        {intro}
        <Button ref={opener} variant="ghost" onClick={() => setOpen(true)}>
          Set up your domain
        </Button>
      </div>
    );
  }
  const shown = normalizeDomain(domain) || 'yourdomain.com';
  const who = asciiName(shopName ?? '');
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (add.isPending) return;
        add.mutate({ domain, fromLocalPart: local });
      }}
    >
      {intro}
      <Field label="Your domain">
        <input
          className={field}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="joesplumbing.com"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="url"
          aria-describedby={error ? `${helpId} ${errorId}` : helpId}
          aria-invalid={error ? true : undefined}
          // The form opened on this press: the cursor goes where the typing starts.
          autoFocus
        />
      </Field>
      <Field label="The address customers see">
        <span className="flex min-w-0 items-center gap-1">
          <input
            // The field's metrics without its full width: the domain beside it gets the rest.
            className={`${field.replace('w-full', '')} w-32 flex-none`}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-describedby={helpId}
          />
          <span className="min-w-0 flex-1 break-all text-sm">@{shown}</span>
        </span>
      </Field>
      <p id={helpId} className="text-xs text-muted-foreground">
        Customers see “{who} &lt;{local.trim().toLowerCase() || 'office'}@{shown}&gt;”,
        and their replies go to that address. Already send email from this domain through
        another service? Use a subdomain, like mail.{shown}.
      </p>
      {error ? (
        <div id={errorId}>
          <Notice tone="danger" alert>
            {error}
          </Notice>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" aria-disabled={add.isPending || undefined}>
          {add.isPending ? 'Adding…' : 'Add domain'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The shop's domain: where it stands, its records, and the owner's actions. */
function DomainPanel({
  v,
  owner,
  focusState,
  onDisconnected,
}: {
  v: SendingDomainView;
  owner: boolean;
  focusState: boolean;
  onDisconnected: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow(1_000);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stateLine = useRef<HTMLParagraphElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusState) stateLine.current?.focus();
  }, [focusState]);
  const refused = (e: { message: string }) => {
    setNote(null);
    setError(fieldErrorOf(e.message));
    void qc.invalidateQueries();
  };
  const checkAgain = useMutation(
    trpc.emailDomain.check.mutationOptions({
      onSuccess: () => {
        setError(null);
        setNote(null);
        void qc.invalidateQueries();
      },
      onError: refused,
    }),
  );
  const test = useMutation(
    trpc.emailDomain.sendTest.mutationOptions({
      onSuccess: (r) => {
        setError(null);
        setNote(domainTestLine(r));
        void qc.invalidateQueries();
      },
      onError: refused,
    }),
  );
  const disconnect = useMutation(
    trpc.emailDomain.disconnect.mutationOptions({
      onSuccess: () => {
        setError(null);
        onDisconnected();
        void qc.invalidateQueries();
      },
      onError: refused,
    }),
  );
  const busy = checkAgain.isPending || test.isPending || disconnect.isPending;
  const wait = checkAgainIn(v.checkAgainAt, now);
  const removal = removeLine(v.removeAt);

  return (
    <div className="space-y-3" data-domain-state={v.state}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 break-all text-sm font-medium">{v.fromAddress}</span>
        <StatusBadge tone={DOMAIN_STATE_TONE[v.state]}>
          {DOMAIN_STATE_BADGE[v.state]}
        </StatusBadge>
      </div>
      <p ref={stateLine} tabIndex={-1} className="text-sm">
        {domainStateLine(v)}
      </p>
      <p className="text-xs text-muted-foreground">
        Customers see “{v.fromLine}”. {lastCheckedLine(v.lastCheckedAt, now)}.
        {removal ? ` ${removal}` : ''}
      </p>
      <Records v={v} />
      {owner ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              aria-disabled={busy || wait !== null || undefined}
              onClick={() => {
                if (busy || wait !== null) return;
                checkAgain.mutate();
              }}
            >
              {checkAgain.isPending ? 'Checking…' : 'Check again'}
            </Button>
            {v.state === 'ready' ? (
              <Button
                variant="ghost"
                aria-disabled={busy || undefined}
                onClick={() => {
                  if (busy) return;
                  test.mutate();
                }}
              >
                {test.isPending ? 'Sending…' : 'Send a test from your domain'}
              </Button>
            ) : null}
            <Button
              ref={opener}
              variant="ghost"
              aria-expanded={confirming}
              onClick={() => setConfirming((c) => !c)}
            >
              Disconnect
            </Button>
          </div>
          {wait !== null ? (
            <p className="text-xs text-muted-foreground" data-check-wait>
              You can check again in {wait} second{wait === 1 ? '' : 's'}.
            </p>
          ) : null}
          {confirming ? (
            <div
              role="group"
              aria-label={`Disconnect ${v.domain}`}
              className="space-y-2 rounded-[var(--radius)] border border-border bg-surface-2 p-2"
            >
              <p className="text-sm">
                Disconnect {v.domain}? Your customer emails go from ServWell’s address,
                and you can then remove the four records from your DNS.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="danger"
                  aria-disabled={busy || undefined}
                  // The step this press opened takes the focus.
                  autoFocus
                  onClick={() => {
                    if (busy) return;
                    disconnect.mutate();
                  }}
                >
                  {disconnect.isPending ? 'Disconnecting…' : `Disconnect ${v.domain}`}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirming(false);
                    opener.current?.focus();
                  }}
                >
                  Keep it
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {note ? (
        <p role="status" className="text-xs">
          {note}
        </p>
      ) : null}
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
    </div>
  );
}

/**
 * The four records, as a DNS panel asks for them: on a narrow card each record is a block of
 * labelled values; on a wide one (`@2xl`, the card's width, not the window's) they line up under
 * one header row like a table. Every value has its own Copy; Copy all takes them together (with
 * the optional DMARC record) for whoever runs the DNS. Values wrap inside their column, never
 * pushing the card sideways.
 */
function Records({ v }: { v: SendingDomainView }) {
  const body = (
    <div className="space-y-2">
      {v.provider ? (
        <p className="text-xs" data-dns-provider={v.provider.id}>
          Your domain’s DNS is at <strong>{v.provider.name}</strong>. {v.provider.hint}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground" data-dns-host-hint>
        {dnsHostHint(v.records)}
      </p>
      <div className="flex justify-end">
        <CopyButton
          size="sm"
          value={recordsText(v.domain, v.records, v.dmarcRecord)}
          label="Copy all"
          ariaLabel={`Copy all ${v.records.length} records`}
          promptLabel="Copy these records"
        />
      </div>
      <div className="@container">
        <div
          aria-hidden="true"
          className="hidden gap-3 border-b border-border pb-1 text-xs font-medium text-muted-foreground @2xl:grid @2xl:grid-cols-[7.5rem_minmax(7rem,11rem)_minmax(0,1fr)_6.5rem]"
        >
          <span>Type</span>
          <span>Host</span>
          <span>Value</span>
          <span>Found?</span>
        </div>
        <ul className="divide-y divide-border" aria-label={`DNS records for ${v.domain}`}>
          {v.records.map((r, i) => (
            <li key={r.purpose} className="py-2" data-record={r.purpose}>
              <RecordRow r={r} n={i + 1} />
            </li>
          ))}
        </ul>
      </div>
      {v.dmarcRecord ? <DmarcSuggestion d={v.dmarcRecord} /> : null}
    </div>
  );
  // A domain that works keeps its records a click away; one being set up shows them.
  return v.state === 'ready' ? (
    <details>
      <summary className="cursor-pointer text-sm font-medium">DNS records</summary>
      <div className="mt-2">{body}</div>
    </details>
  ) : (
    body
  );
}

/** One label + value + Copy, labelled for a screen reader at every width. */
function Cell({
  label,
  value,
  copyLabel,
  inTable = true,
}: {
  label: string;
  value: string;
  copyLabel: string;
  /** In the records' table, the header row says the label on a wide card. */
  inTable?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className={`text-xs text-muted-foreground ${inTable ? '@2xl:sr-only' : ''}`}>
        {label}
      </dt>
      <dd className="flex min-w-0 items-start gap-1">
        <span className="min-w-0 flex-1 break-all font-mono text-xs">{value}</span>
        <CopyButton size="sm" value={value} ariaLabel={copyLabel} promptLabel={label} />
      </dd>
    </div>
  );
}

function RecordRow({ r, n }: { r: DomainRecordRow; n: number }) {
  const what = `record ${n} (${r.type})`;
  const status = RECORD_STATUS[r.status];
  return (
    <dl className="grid gap-2 @2xl:grid-cols-[7.5rem_minmax(7rem,11rem)_minmax(0,1fr)_6.5rem] @2xl:gap-3">
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground @2xl:sr-only">Type</dt>
        <dd className="text-xs">
          <span className="font-mono font-medium">{r.type}</span>
          <span className="block text-muted-foreground">{RECORD_PURPOSE[r.purpose]}</span>
        </dd>
      </div>
      <Cell label="Host" value={r.host} copyLabel={`Copy the host of ${what}`} />
      <div className="min-w-0 space-y-1">
        <Cell label="Value" value={r.value} copyLabel={`Copy the value of ${what}`} />
        {r.priority !== null ? (
          <Cell
            label="Priority"
            value={String(r.priority)}
            copyLabel={`Copy the priority of ${what}`}
            // No column of its own: its label stays, at every width.
            inTable={false}
          />
        ) : null}
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground @2xl:sr-only">Found?</dt>
        <dd>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </dd>
      </div>
    </dl>
  );
}

/** The optional DMARC record (D rec 8), only while the domain publishes none. */
function DmarcSuggestion({ d }: { d: NonNullable<SendingDomainView['dmarcRecord']> }) {
  return (
    <div
      className="space-y-2 rounded-[var(--radius)] border border-dashed border-border p-2"
      data-dmarc-suggestion
    >
      <p className="text-xs">
        <strong>Optional:</strong> your domain has no DMARC record. Adding this one helps
        your emails reach inboxes, and changes nothing else.
      </p>
      <dl className="grid gap-2">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">Type</dt>
          <dd className="font-mono text-xs font-medium">TXT</dd>
        </div>
        <Cell
          label="Host"
          value={d.host}
          copyLabel="Copy the host of the DMARC record"
          inTable={false}
        />
        <Cell
          label="Value"
          value={d.value}
          copyLabel="Copy the value of the DMARC record"
          inTable={false}
        />
      </dl>
    </div>
  );
}
